/**
 * localStorage key registry and naming convention for the whole frontend.
 *
 * Convention: every key is `<prefix>:<scope>:<name>` — prefix `medialib`, `scope` the
 * owning app (`portal` or a pilet name), `name` a snake_case identifier. The shell and
 * every pilet share one origin, so an un-namespaced key would collide in localStorage;
 * routing every key through {@link storageKey} keeps them disjoint and greppable.
 *
 * This module is the home for keys that CROSS a package boundary — the shell writes
 * and a pilet reads (`user`, `session`, `device_id`). Pilets depend on the styleguide,
 * not on each other or the shell, so a shared key has nowhere else to live. Keys used inside a single package live in that package's own
 * `src/storage.ts` instead.
 *
 * NOTE: `src/storage.ts` is listed in this package's `sideEffects` so the one-time
 * key migrations below are never tree-shaken away.
 */
export const STORAGE_PREFIX = 'medialib'

/** Build a convention-compliant localStorage key: `medialib:<scope>:<name>`. */
export function storageKey (scope: string, name: string): string {
  return `${STORAGE_PREFIX}:${scope}:${name}`
}

/**
 * One-time move of a value from a legacy key to its convention-compliant key. Runs at
 * module load so it lands before any `use-persisted-state` hook first reads the new
 * key. Idempotent: a no-op once the new key holds a value (so it is safe across
 * reloads and no matter which package imports the key first), and when storage is
 * unavailable.
 */
export function migrateStorageKey (legacyKey: string, key: string): void {
  try {
    if (window.localStorage.getItem(key) !== null) return
    const legacy = window.localStorage.getItem(legacyKey)
    if (legacy === null) return
    window.localStorage.setItem(key, legacy)
    window.localStorage.removeItem(legacyKey)
  } catch {
    // localStorage unavailable (private mode / blocked) — nothing to migrate.
  }
}

/**
 * The signed-in identity — written by the shell's AuthProvider from `whoami`, read by
 * the favorites/watch-later toggles and the profile pilet. Scoped to `portal`, its
 * single writer.
 */
export const USER_STORAGE_KEY = storageKey('portal', 'user')

/**
 * The full whoami session (roles + quota) — shell-written, pilet-read, the sibling of
 * {@link USER_STORAGE_KEY}.
 */
export const SESSION_STORAGE_KEY = storageKey('portal', 'session')

/**
 * The current device's public id — the backend-generated id returned by device
 * enrollment, a PUBLIC value, not secret key material (the private device key stays
 * non-extractable in IndexedDB). Written by the shell (see the portal's device
 * enrollment), read by the profile pilet's device list to mark the current browser.
 * Scoped to `portal`, its writer.
 */
export const DEVICE_ID_STORAGE_KEY = storageKey('portal', 'device_id')

// Move the legacy un-namespaced keys to the convention once, at module load (before
// any hook reads them). Co-located with the definitions so importing any of these
// keys — from the shell or any pilet, in whatever load order — triggers the migration.
migrateStorageKey('user', USER_STORAGE_KEY)
migrateStorageKey('session', SESSION_STORAGE_KEY)

// NOTE: the favorites and watch-later buckets (`medialib:favorites:list`,
// `medialib:watchlater:list`) used to live here. Those lists are account data on the
// server now (`/api/3.0/profile/list…`, owned by the shell — see the portal's
// `src/lists/`), so the keys, their migrations and their toggles are gone rather than
// kept as a fallback: two stores where one is authoritative is how the toggles drift
// apart. The old buckets are deliberately NOT migrated — a viewer's list starts empty
// on the API (see documentation/spec-user-lists-api.md).
